import { useState, useEffect, useCallback, useRef } from 'react';

const STORAGE_KEY = 'dashboard_custom_widgets';

const useCustomWidgets = (selectedBrand) => {
  // Keep track of initial load
  const isInitialMount = useRef(true);
  
  // Load widgets from localStorage
  const loadWidgetsFromStorage = useCallback((brandId) => {
    if (!brandId) {
      return [];
    }
    
    const storageKey = `${STORAGE_KEY}_${brandId}`;
    
    try {
      const stored = localStorage.getItem(storageKey);
      
      if (stored) {
        const parsedWidgets = JSON.parse(stored);
        return parsedWidgets;
      }
    } catch (error) {
      console.error('Error loading widgets from localStorage:', error);
    }
    
    return [];
  }, []);

  // Initialize state with loaded widgets
  const [customWidgets, setCustomWidgets] = useState(() => {
    return loadWidgetsFromStorage(selectedBrand);
  });

  // Save widgets to localStorage whenever they change
  useEffect(() => {
    // Skip saving on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    
    if (selectedBrand) {
      try {
        const storageKey = `${STORAGE_KEY}_${selectedBrand}`;
        
        if (customWidgets.length > 0) {
          // Only save widget metadata, not component functions
          const widgetsToStore = customWidgets.map(widget => ({
            id: widget.id,
            key: widget.key,
            title: widget.title,
            icon: widget.icon,
            description: widget.description,
            settings: widget.settings,
            defaultSettings: widget.defaultSettings
          }));
          
          localStorage.setItem(storageKey, JSON.stringify(widgetsToStore));
        } else {
          // If no widgets, remove from localStorage
          localStorage.removeItem(storageKey);
        }
      } catch (error) {
        console.error('Error saving widgets to localStorage:', error);
      }
    }
  }, [customWidgets, selectedBrand]);

  // Handle brand changes
  useEffect(() => {
    if (!isInitialMount.current && selectedBrand) {
      const loadedWidgets = loadWidgetsFromStorage(selectedBrand);
      setCustomWidgets(loadedWidgets);
    }
  }, [selectedBrand, loadWidgetsFromStorage]);

  const addWidget = useCallback((widget) => {
    const newWidget = {
      key: widget.key,
      id: `custom-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      title: widget.title,
      icon: widget.icon,
      description: widget.description,
      settings: { ...(widget.defaultSettings || {}), ...(widget.settings || {}) },
      defaultSettings: widget.defaultSettings
    };
    
    setCustomWidgets(prev => {
      const updated = [...prev, newWidget];
      return updated;
    });
    
    return newWidget;
  }, []);

  const removeWidget = useCallback((widgetId) => {
    setCustomWidgets(prev => prev.filter(w => w.id !== widgetId));
  }, []);

  const updateWidgetSettings = useCallback((widgetId, newSettings) => {
    setCustomWidgets(prev => prev.map(w => 
      w.id === widgetId ? { ...w, settings: newSettings } : w
    ));
  }, []);

  const reorderWidgets = useCallback((startIndex, endIndex) => {
    setCustomWidgets(prev => {
      const result = Array.from(prev);
      const [removed] = result.splice(startIndex, 1);
      result.splice(endIndex, 0, removed);
      return result;
    });
  }, []);

  const clearAllWidgets = useCallback(() => {
    setCustomWidgets([]);
    
    if (selectedBrand) {
      const storageKey = `${STORAGE_KEY}_${selectedBrand}`;
      localStorage.removeItem(storageKey);
    }
  }, [selectedBrand]);

  return {
    customWidgets,
    addWidget,
    removeWidget,
    updateWidgetSettings,
    reorderWidgets,
    clearAllWidgets
  };
};

export default useCustomWidgets;